import '@/styles/astro/new-design.css';
import JosephWaters from '@listening/assets/astro/images/Frame-1362789785-2_1.png';
import AubreyWilliams from '@listening/assets/astro/images/Frame-1362789785-3_1.png';
import SeanGruber from '@listening/assets/astro/images/new-design-25/ui/avatar-testimonial-1.png';
import ErinBarnes from '@listening/assets/astro/images/new-design-25/ui/avatar-testimonial-2.png';
import RóisínHennessy from '@listening/assets/astro/images/new-design-25/ui/avatar-testimonial-3.png';
import MandyEversole from '@listening/assets/astro/images/new-design-25/ui/avatar-testimonial-4.png';
import BreaT from '@listening/assets/astro/images/new-design-25/ui/avatar-testimonial-5.png';
import MaryBallardJohansson from '@listening/assets/astro/images/new-design-25/ui/avatar-testimonial-6.png';
import DavidHoeffer from '@listening/assets/astro/images/new-design-25/ui/avatar-testimonial-7.png';
import ReadE from '@listening/assets/astro/images/new-design-25/ui/avatar-testimonial-8.png';
import SamanthaLumby from '@listening/assets/astro/images/new-design-25/ui/avatar-testimonial-9.png';
import BaobeiDeBei from '@listening/assets/astro/images/new-design-25/ui/avatar-testimonial-10.png';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

export function SeoContent(props: {
  sections?: Array<{
    title: string;
    content: string;
    contentSecondary?: string;
  }>;
  autoRotate?: boolean;
  rotationInterval?: number;
}) {
  const sections = props.sections ?? [];
  const autoRotate = props.autoRotate ?? true;
  const rotationInterval = props.rotationInterval ?? 5000;

  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);

  useEffect(() => {
    if (!autoRotate || sections.length <= 1 || isHovered || userInteracted)
      return;

    const interval = setInterval(() => {
      setCurrentCardIndex((prev) => (prev + 1) % sections.length);
    }, rotationInterval);

    return () => {
      clearInterval(interval);
    };
  }, [
    sections.length,
    autoRotate,
    rotationInterval,
    isHovered,
    userInteracted,
  ]);

  useEffect(() => {
    if (!userInteracted) return;

    const timeout = setTimeout(() => {
      setUserInteracted(false);
    }, rotationInterval * 2);

    return () => {
      clearTimeout(timeout);
    };
  }, [userInteracted, rotationInterval]);

  const handleManualCardChange = (index: number) => {
    setCurrentCardIndex(index);
    setUserInteracted(true);
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    },
    exit: {
      opacity: 0,
      y: -50,
      scale: 0.95,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    },
  };

  const contentVariants = {
    hidden: {
      opacity: 0,
      x: 20,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        delay: 0.2,
        ease: 'easeOut' as const,
      },
    },
  };

  if (sections.length === 0) return null;

  const currentSection = sections[currentCardIndex];
  if (!currentSection) return null;

  return (
    <section className='py-12 md:py-16 lg:py-20'>
      <div className='mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8'>
        <div className='relative min-h-[300px] md:min-h-[400px]'>
          <AnimatePresence mode='wait'>
            <motion.div
              animate='visible'
              className='relative overflow-hidden rounded-2xl bg-white shadow-xl md:rounded-[32px]'
              exit='exit'
              initial='hidden'
              key={currentCardIndex}
              onMouseEnter={() => {
                setIsHovered(true);
              }}
              onMouseLeave={() => {
                setIsHovered(false);
              }}
              variants={cardVariants}
              whileHover={{
                scale: 1.02,
                y: -8,
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: {
                  duration: 0.3,
                  ease: [0.25, 0.46, 0.45, 0.94] as const,
                },
              }}
            >
              <div className='absolute inset-0 bg-gradient-to-r from-blue-400/20 via-purple-400/30 to-pink-400/20 p-[1.5px]'>
                <div className='h-full w-full rounded-[calc(0.5rem-1.5px)] bg-white md:rounded-[30px]' />
              </div>

              <div className='relative z-10 p-6 sm:p-8 md:p-12 lg:p-14'>
                <motion.h2
                  animate='visible'
                  className='mb-6 text-center text-lg leading-tight font-bold tracking-tight text-gray-900 sm:text-xl md:mb-8 md:text-2xl lg:text-[32px]'
                  initial='hidden'
                  variants={contentVariants}
                >
                  {currentSection.title}
                </motion.h2>

                <motion.div
                  animate='visible'
                  className='flex flex-col items-stretch justify-center gap-4 sm:gap-6 lg:flex-row lg:gap-8 xl:gap-12'
                  initial='hidden'
                  variants={contentVariants}
                >
                  {currentSection.content && (
                    <div className='flex min-h-24 w-full items-center justify-center p-4 md:min-h-32 md:p-6 lg:w-1/2'>
                      <p className='text-center text-sm leading-relaxed font-medium text-gray-700 sm:text-base'>
                        {currentSection.content}
                      </p>
                    </div>
                  )}

                  {currentSection.contentSecondary && (
                    <div className='flex min-h-24 w-full items-center justify-center rounded-2xl bg-gradient-to-r from-blue-400/20 via-purple-400/30 to-pink-400/20 p-4 md:min-h-32 md:rounded-3xl md:p-6 lg:w-1/2'>
                      <p className='text-center text-sm leading-relaxed font-medium text-gray-700 sm:text-base'>
                        {currentSection.contentSecondary}
                      </p>
                    </div>
                  )}
                </motion.div>

                <motion.div
                  animate='visible'
                  className='mt-8 border-t border-gray-200/60 pt-8 md:mt-12 md:pt-12 lg:mt-16'
                  initial='hidden'
                  variants={contentVariants}
                >
                  <div className='flex flex-col items-center justify-center gap-6 lg:flex-row lg:gap-8 xl:gap-12'>
                    <div className='order-2 grid grid-cols-3 gap-2 sm:grid-cols-6 lg:order-1 lg:flex lg:gap-0 lg:-space-x-4'>
                      {(currentCardIndex === 0
                        ? [
                            ErinBarnes,
                            MandyEversole,
                            MaryBallardJohansson,
                            DavidHoeffer,
                            ReadE,
                            SeanGruber,
                          ]
                        : [
                            RóisínHennessy,
                            BreaT,
                            SamanthaLumby,
                            BaobeiDeBei,
                            JosephWaters,
                            AubreyWilliams,
                          ]
                      ).map((avatar, avatarIndex) => (
                        <motion.div
                          animate={{ opacity: 1, scale: 1 }}
                          className='h-10 w-10 overflow-hidden rounded-full border-2 border-white shadow-sm sm:h-12 sm:w-12 lg:border-[3px]'
                          initial={{ opacity: 0, scale: 0.8 }}
                          key={`avatar-${currentCardIndex.toString()}-${avatarIndex.toString()}`}
                          transition={{
                            duration: 0.3,
                            delay: 0.4 + avatarIndex * 0.1,
                            ease: 'easeOut' as const,
                          }}
                        >
                          <img
                            alt={`Profile ${(avatarIndex + 1).toString()}`}
                            className='h-full w-full object-cover'
                            src={avatar}
                          />
                        </motion.div>
                      ))}
                    </div>

                    <p className='order-1 max-w-md text-center text-sm leading-relaxed font-medium text-gray-700 sm:text-base lg:order-2 lg:max-w-none lg:text-left'>
                      {currentCardIndex === 0
                        ? 'Students report better comprehension with audio, saying it helps them grasp concepts rather than just memorize facts.'
                        : 'Researchers find our platform saves hours of reading time while maintaining deep understanding of complex academic content.'}
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Card Navigation Indicators */}
          {sections.length > 1 && (
            <motion.div
              animate={{ opacity: 1 }}
              className='mt-6 flex justify-center gap-2 md:mt-8 md:gap-3'
              initial={{ opacity: 0 }}
              transition={{ delay: 0.8 }}
            >
              {sections.map((section, index) => (
                <button
                  type='button'
                  aria-label={`Go to section ${String(index + 1)}`}
                  className={`h-2.5 w-2.5 rounded-full transition-all duration-300 md:h-3 md:w-3 ${
                    index === currentCardIndex
                      ? 'scale-125 bg-blue-500'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  key={section.title}
                  onClick={() => {
                    handleManualCardChange(index);
                  }}
                />
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
